export type SceneSyncKind = 'none' | 'palette' | 'floors' | 'selection' | 'land' | 'entities' | 'full';

export interface SceneLifecycleCounts {
  hostCreates: number;
  parkRebuilds: number;
  floorSyncs: number;
  selectionSyncs: number;
  landSyncs: number;
  entitySyncs: number;
}

export function emptySceneLifecycleCounts(): SceneLifecycleCounts {
  return {
    hostCreates: 0,
    parkRebuilds: 0,
    floorSyncs: 0,
    selectionSyncs: 0,
    landSyncs: 0,
    entitySyncs: 0,
  };
}

/** Scene host is created once at boot. Interaction sync never remounts it. */
export function recreatesSceneHost(_kind: SceneSyncKind): boolean {
  return false;
}

export function recordsParkRebuild(kind: SceneSyncKind): boolean {
  return kind === 'full';
}

/**
 * Proven interaction contract: HUD-only and overlay-only actions must not
 * rebuild the park root. Common entity mutations use entity-scoped sync.
 */
export function interactionSceneSync(
  event:
    | { type: 'sound' }
    | { type: 'language' }
    | { type: 'closePanel' }
    | { type: 'floorPaint' }
    | { type: 'toggleBuild'; leavingLand: boolean; clearingSelection: boolean }
    | { type: 'toggleFloor'; leavingLand: boolean }
    | { type: 'toggleLand' }
    | { type: 'selectLand' }
    | { type: 'worldMutation' },
): SceneSyncKind {
  switch (event.type) {
    case 'sound':
    case 'language':
      return 'none';
    case 'closePanel':
      return 'selection';
    case 'floorPaint':
      return 'floors';
    case 'toggleLand':
      return 'land';
    case 'selectLand':
      return 'none';
    case 'toggleBuild':
      if (event.leavingLand) {
        return 'land';
      }
      return event.clearingSelection ? 'selection' : 'none';
    case 'toggleFloor':
      return event.leavingLand ? 'land' : 'selection';
    case 'worldMutation':
      return 'entities';
  }
}

export function applySceneSyncKind(
  kind: SceneSyncKind,
  host: {
    rebuild(): void;
    syncFloors(): void;
    syncSelection(): void;
    syncLandOverlays(): void;
    syncEntities(): void;
    applyApprovedPalettes(): void;
  },
  counts: SceneLifecycleCounts,
): void {
  if (kind === 'full') {
    host.rebuild();
    counts.parkRebuilds += 1;
    return;
  }
  if (kind === 'entities') {
    host.syncEntities();
    counts.entitySyncs += 1;
    return;
  }
  if (kind === 'floors') {
    host.syncFloors();
    counts.floorSyncs += 1;
    return;
  }
  if (kind === 'selection') {
    host.syncSelection();
    counts.selectionSyncs += 1;
    return;
  }
  if (kind === 'land') {
    host.syncLandOverlays();
    counts.landSyncs += 1;
    return;
  }
  if (kind === 'palette') {
    host.applyApprovedPalettes();
  }
}
